/**
 * Author-level citation metrics from a list of per-paper citation counts.
 *
 * - h-index (Hirsch 2005): the largest h such that h papers have at least h citations
 *   each.
 * - g-index (Egghe 2006): the largest g such that the g most-cited papers have at least
 *   g² citations between them. Egghe's definition can exceed the number of papers if
 *   uncited "phantom" papers are imagined at the end; the usual convention caps g at
 *   the number of papers listed, and the uncapped value is reported beside it.
 * - i10-index (Google Scholar): papers with at least 10 citations.
 * - m-quotient (Hirsch 2005): h divided by the years since the first publication.
 */

export interface Paper {
  /** 1-based line in the pasted text. */
  line: number;
  title: string | null;
  citations: number;
}

export interface Rejected {
  line: number;
  text: string;
}

export interface ParsedPapers {
  papers: Paper[];
  rejected: Rejected[];
}

const RE_NEWLINE = /\r?\n/;
const RE_LETTER = /\p{L}/u;
/** "Title, 45", "Title<TAB>45", "Title; 1,234": the count is the last field. */
const RE_TITLED =
  /^(.*?\S)\s*[,;\t|]\s*(\d{1,3}[,.\u00A0\u202F]\d{3}|\d+)\s*$/u;
/**
 * One count with a thousands separator: 1,234 · 1.234 · 1 234 (no-break space). Only
 * one separator: no paper has a million citations, so "45,120,300" is three counts,
 * not one.
 */
const RE_GROUPED = /^\d{1,3}[,.\u00A0\u202F]\d{3}$/u;
const RE_DIGITS = /^\d+$/;
const RE_NUMBER_SEPARATORS = /[\s,;|]+/;
const RE_GROUP_MARKS = /[,.\u00A0\u202F]/gu;

/**
 * A citation count as typed. Counts are whole numbers, so a separator before a group
 * of three digits can only be a thousands separator — "1,234" and German "1.234" are
 * both 1,234 — which is why this does not use the decimal-aware number parser (it
 * reads a lone separator as a decimal point, turning "1.234" into 1).
 */
export function parseCount(raw: string): number | null {
  const value = raw.trim();
  if (RE_DIGITS.test(value)) {
    return Number(value);
  }
  if (RE_GROUPED.test(value)) {
    return Number(value.replace(RE_GROUP_MARKS, ""));
  }
  return null;
}

function numbersOnly(text: string): number[] | null {
  const single = parseCount(text);
  if (single !== null) {
    return [single];
  }
  const counts = text
    .split(RE_NUMBER_SEPARATORS)
    .filter(Boolean)
    .map(parseCount);
  return counts.every((count) => count !== null) ? (counts as number[]) : null;
}

/**
 * One count per line, or "title, count" (comma, tab, semicolon or bar before the
 * count). A line of bare numbers may hold several, separated by spaces or commas.
 */
export function parseCitationList(text: string): ParsedPapers {
  const papers: Paper[] = [];
  const rejected: Rejected[] = [];
  for (const [index, raw] of text.split(RE_NEWLINE).entries()) {
    const line = raw.trim();
    if (line === "") {
      continue;
    }
    if (!RE_LETTER.test(line)) {
      const counts = numbersOnly(line);
      if (counts) {
        for (const citations of counts) {
          papers.push({ line: index + 1, title: null, citations });
        }
        continue;
      }
    }
    const titled = RE_TITLED.exec(line);
    const citations = titled ? parseCount(titled[2]) : null;
    if (titled && citations !== null) {
      papers.push({ line: index + 1, title: titled[1].trim(), citations });
    } else {
      rejected.push({ line: index + 1, text: line });
    }
  }
  return { papers, rejected };
}

export interface HMetrics {
  papers: number;
  total: number;
  mean: number;
  median: number;
  h: number;
  /** g capped at the number of papers listed. */
  g: number;
  /** Egghe's uncapped g, counting imagined uncited papers past the end. */
  gUncapped: number;
  i10: number;
  /** Most-cited first; ties keep the order they were pasted in. */
  ranked: Paper[];
  /** Citations to the h-core papers. */
  hCoreCitations: number;
}

export function hIndex(descending: readonly number[]): number {
  let h = 0;
  for (const [index, citations] of descending.entries()) {
    if (citations >= index + 1) {
      h = index + 1;
    } else {
      break;
    }
  }
  return h;
}

export function gIndex(descending: readonly number[]): {
  g: number;
  gUncapped: number;
} {
  let sum = 0;
  let g = 0;
  for (const [index, citations] of descending.entries()) {
    sum += citations;
    if (sum >= (index + 1) ** 2) {
      g = index + 1;
    }
  }
  const gUncapped =
    g === descending.length && g > 0 ? Math.floor(Math.sqrt(sum)) : g;
  return { g, gUncapped };
}

function median(sorted: readonly number[]): number {
  if (sorted.length === 0) {
    return 0;
  }
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function citationMetrics(papers: readonly Paper[]): HMetrics {
  const ranked = [...papers].sort(
    (a, b) => b.citations - a.citations || a.line - b.line
  );
  const counts = ranked.map((paper) => paper.citations);
  const total = counts.reduce((sum, count) => sum + count, 0);
  const h = hIndex(counts);
  const { g, gUncapped } = gIndex(counts);
  return {
    papers: counts.length,
    total,
    mean: counts.length === 0 ? 0 : total / counts.length,
    median: median([...counts].reverse()),
    h,
    g,
    gUncapped,
    i10: counts.filter((count) => count >= 10).length,
    ranked,
    hCoreCitations: counts.slice(0, h).reduce((sum, count) => sum + count, 0),
  };
}

export type YearCount = "elapsed" | "inclusive";

/**
 * Hirsch's m: h ÷ n, with n the years since the first paper. "elapsed" counts
 * current − first (Hirsch's wording); "inclusive" counts both end years, as some
 * profiles do. Null when n would be below one.
 */
export function mQuotient(
  h: number,
  firstYear: number,
  currentYear: number,
  count: YearCount
): { years: number; m: number } | null {
  const years = currentYear - firstYear + (count === "inclusive" ? 1 : 0);
  if (!Number.isFinite(years) || years < 1) {
    return null;
  }
  return { years, m: h / years };
}
